/**
 * WordPress dependencies
 */
import { useEffect, useId, useState } from '@safe-wordpress/element';

/**
 * Internal dependencies
 */
import './style.scss';

export type CircularProgressProps = {
	readonly value: number | string;
	readonly percentage: number;
	readonly color: 'disabled' | 'bad' | 'ok' | 'good';
	readonly title: string;
	readonly explanation: string | JSX.Element;
	readonly animationFrom?: 'zero' | 'full';
	readonly showPercent?: boolean;
	readonly size?: number;
	readonly strokeWidth?: number;
};

export function CircularProgress( {
	value,
	percentage,
	color,
	title,
	explanation,
	animationFrom = 'zero',
	showPercent = false,
	size = 48,
	strokeWidth = 4,
}: CircularProgressProps ): JSX.Element {
	value = `${ value }`;
	const id = useId();
	const [ animatedPercentage, setAnimatedPercentage ] = useState( 0 );

	const safePercentage = Math.max( 0, Math.min( 100, percentage ) );

	const radius = ( size - strokeWidth ) / 2;
	const circumference = 2 * Math.PI * radius;
	const dashOffset =
		circumference - ( animatedPercentage / 100 ) * circumference;

	useEffect( () => {
		setAnimatedPercentage( animationFrom === 'full' ? 100 : 0 );

		const timeout = window.setTimeout( () => {
			setAnimatedPercentage( safePercentage );
		}, 50 );

		return () => window.clearTimeout( timeout );
	}, [ safePercentage, animationFrom ] );

	return (
		<section
			className={ `nab-circular-progress nab-circular-progress--${ color }` }
			aria-labelledby={ `${ id }-title` }
		>
			<div
				className="nab-circular-progress__graphic"
				role="progressbar"
				aria-valuemin={ 0 }
				aria-valuemax={ 100 }
				aria-valuenow={ safePercentage }
				aria-label={ title }
				style={ { fontSize: `calc(${ size }px + 3rem)` } }
			>
				<svg
					className="nab-circular-progress__svg"
					viewBox={ `0 0 ${ size } ${ size }` }
					aria-hidden="true"
				>
					<circle
						className="nab-circular-progress__track"
						cx={ size / 2 }
						cy={ size / 2 }
						r={ radius }
						strokeWidth={ strokeWidth }
					/>
					<circle
						className="nab-circular-progress__bar"
						cx={ size / 2 }
						cy={ size / 2 }
						r={ radius }
						strokeWidth={ strokeWidth }
						strokeDasharray={ circumference }
						strokeDashoffset={ dashOffset }
					/>
				</svg>

				<div className="nab-circular-progress__content">
					<strong className="nab-circular-progress__value">
						{ value }
						{ !! showPercent && <span>%</span> }
					</strong>
				</div>
			</div>

			<div className="nab-circular-progress__body">
				<div
					id={ `${ id }-title` }
					className="nab-circular-progress__title"
				>
					{ title }
				</div>
				<div className="nab-circular-progress__explanation">
					{ explanation }
				</div>
			</div>
		</section>
	);
}
